import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { tasksAPI, usersAPI } from '../../services/api';
import { useRole } from '../../hooks/useRole';
import DailyUpdateForm from '../../components/tasks/DailyUpdateForm';
import SubtaskPanel from '../../components/tasks/SubtaskPanel';
import TimeLogPanel from '../../components/tasks/TimeLogPanel';
import DependencyPicker from '../../components/tasks/DependencyPicker';

// ── Colours ───────────────────────────────────────────────────────────────────
const PRIORITY_COLORS = {
  Critical: { bg: '#fee2e2', text: '#dc2626' },
  High:     { bg: '#ffedd5', text: '#ea580c' },
  Medium:   { bg: '#dbeafe', text: '#2563eb' },
  Low:      { bg: '#f1f5f9', text: '#64748b' },
};
const STATUS_COLORS = {
  'To Do':       { bg: '#f1f5f9', text: '#475569' },
  'In Progress': { bg: '#fef3c7', text: '#d97706' },
  'Review':      { bg: '#ede9fe', text: '#7c3aed' },
  'Done':        { bg: '#dcfce7', text: '#16a34a' },
  'Cancelled':   { bg: '#f3f4f6', text: '#6b7280' },
};
const STATUSES   = ['To Do', 'In Progress', 'Review', 'Done', 'Cancelled'];
const PRIORITIES = ['Critical', 'High', 'Medium', 'Low'];

const TABS = [
  { id: 'overview',  label: 'Overview',     icon: '📋' },
  { id: 'updates',   label: 'Daily Updates', icon: '📝' },
  { id: 'subtasks',  label: 'Subtasks',      icon: '✅' },
  { id: 'timelog',   label: 'Time Log',      icon: '⏱️' },
  { id: 'files',     label: 'Files',         icon: '📎' },
  { id: 'activity',  label: 'Activity Log',  icon: '📜' },
];

const VIEWABLE_TYPES = new Set(['pdf', 'png', 'jpg', 'jpeg', 'txt']);

function fileIcon(ext) {
  if (ext === 'pdf') return '📄';
  if (['png','jpg','jpeg'].includes(ext)) return '🖼️';
  if (['doc','docx'].includes(ext)) return '📝';
  if (['xls','xlsx'].includes(ext)) return '📊';
  return '📎';
}

function formatBytes(b) {
  if (!b) return '—';
  if (b < 1024) return b + ' B';
  if (b < 1024 * 1024) return (b / 1024).toFixed(1) + ' KB';
  return (b / 1024 / 1024).toFixed(1) + ' MB';
}

function Badge({ bg, text, children }) {
  return <span style={{ background: bg, color: text, borderRadius: 5, padding: '2px 9px', fontSize: 12, fontWeight: 600 }}>{children}</span>;
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function TaskDetail() {
  const { id }   = useParams();
  const navigate = useNavigate();
  const { isSupport, isAdminOrAbove, isManager, user } = useRole();

  const [task,      setTask]      = useState(null);
  const [updates,   setUpdates]   = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [users,     setUsers]     = useState([]);

  // Overview edit state
  const [editing,         setEditing]         = useState(false);
  const [editForm,        setEditForm]        = useState({});
  const [saving,          setSaving]          = useState(false);
  const [circularDepError, setCircularDepError] = useState(false);

  // Files state
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const loadTask = useCallback(async () => {
    try {
      const res = await tasksAPI.getTask(id);
      const data = res.data?.data;
      setTask(data?.task || null);
      setUpdates(data?.updates || []);
      setEditForm({
        title:       data?.task?.title || '',
        description: data?.task?.description || '',
        status:      data?.task?.status || 'To Do',
        priority:    data?.task?.priority || 'Medium',
        assigned_to: data?.task?.assigned_to?._id || '',
        due_date:    data?.task?.due_date ? data.task.due_date.slice(0, 10) : '',
        tags:        data?.task?.tags || [],
        estimated_hours: data?.task?.estimated_hours || '',
        depends_on:  data?.task?.depends_on || [],
      });
    } catch (err) {
      if (err.response?.status === 404 || err.response?.status === 403) {
        navigate('/tasks');
      } else {
        toast.error('Failed to load task');
      }
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { loadTask(); }, [loadTask]);

  useEffect(() => {
    usersAPI.getAll({ limit: 200 }).then(r => setUsers(r.data?.data?.users || [])).catch(() => {});
  }, []);

  // Save edits
  const handleSave = async () => {
    setSaving(true);
    setCircularDepError(false);
    try {
      const payload = {
        title:       editForm.title,
        description: editForm.description || undefined,
        status:      editForm.status,
        priority:    editForm.priority,
        assigned_to: editForm.assigned_to || undefined,
        due_date:    editForm.due_date || undefined,
        estimated_hours: editForm.estimated_hours ? Number(editForm.estimated_hours) : undefined,
        tags:        editForm.tags,
        depends_on:  editForm.depends_on?.map(d => d._id || d) || [],
      };
      await tasksAPI.updateTask(id, payload);
      toast.success('Task updated');
      setEditing(false);
      loadTask();
    } catch (err) {
      const msg = err.response?.data?.message || '';
      if (msg.toLowerCase().includes('circular')) {
        setCircularDepError(true);
      } else {
        toast.error(msg || 'Failed to update task');
      }
    } finally {
      setSaving(false);
    }
  };

  // File upload
  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await tasksAPI.uploadAttachment(id, fd);
      toast.success('File uploaded');
      loadTask();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // View file (blob URL in new tab)
  const handleView = async (attachment) => {
    const win = window.open('', '_blank');
    try {
      const res = await tasksAPI.viewAttachment(id, attachment._id);
      const url = URL.createObjectURL(res.data);
      win.location.href = url;
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      win?.close();
      toast.error('Could not open file');
    }
  };

  // Download file
  const handleDownload = async (attachment) => {
    try {
      const res = await tasksAPI.downloadAttachment(id, attachment._id);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = attachment.original_name || 'file';
      document.body.appendChild(a); a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Download failed');
    }
  };

  // Delete attachment
  const handleDeleteAttachment = async (attachment) => {
    if (!window.confirm(`Delete "${attachment.original_name}"?`)) return;
    try {
      await tasksAPI.deleteAttachment(id, attachment._id);
      toast.success('Attachment deleted');
      loadTask();
    } catch {
      toast.error('Delete failed');
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '80px 0' }}>
        <div style={{ width: 40, height: 40, border: '3px solid #C9A84C', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 16px' }} />
        <div style={{ color: '#94a3b8', fontWeight: 600 }}>Loading task…</div>
      </div>
    );
  }

  if (!task) return null;

  const pColors = PRIORITY_COLORS[task.priority] || PRIORITY_COLORS.Low;
  const sColors = STATUS_COLORS[task.status] || STATUS_COLORS['To Do'];
  const pct = task.completion_percentage || 0;

  return (
    <div style={{ maxWidth: 980, margin: '0 auto' }}>
      {/* ── Back + Header ─────────────────────────────────────────────────── */}
      <div style={{ marginBottom: 16 }}>
        <button onClick={() => navigate('/tasks')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', fontSize: 13, padding: 0, display: 'flex', alignItems: 'center', gap: 4, marginBottom: 12 }}>
          ← Back to Tasks
        </button>
        <div style={{ background: '#fff', borderRadius: 12, padding: '18px 22px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', border: '1px solid #f1f5f9' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, justifyContent: 'space-between', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                <span style={{ fontSize: 12, color: '#94a3b8', fontFamily: 'monospace' }}>{task.task_number}</span>
                <Badge bg={pColors.bg} text={pColors.text}>{task.priority}</Badge>
                <Badge bg={sColors.bg} text={sColors.text}>{task.status}</Badge>
              </div>
              <h1 style={{ fontSize: 20, fontWeight: 800, color: '#1B2A6B', margin: 0, lineHeight: 1.3 }}>{task.title}</h1>
              {task.category && <span style={{ marginTop: 6, display: 'inline-block', fontSize: 12, background: '#f1f5f9', color: '#475569', borderRadius: 4, padding: '2px 8px' }}>{task.category}</span>}
            </div>
            {/* Progress */}
            <div style={{ textAlign: 'right', flexShrink: 0 }}>
              <div style={{ fontSize: 24, fontWeight: 800, color: '#1B2A6B' }}>{pct}%</div>
              <div style={{ fontSize: 11, color: '#94a3b8' }}>Complete</div>
              <div style={{ width: 100, height: 6, background: '#f1f5f9', borderRadius: 3, marginTop: 4, overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${pct}%`, background: pct === 100 ? '#22c55e' : '#1B2A6B', borderRadius: 3 }} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Tabs ──────────────────────────────────────────────────────────── */}
      <div style={{ background: '#fff', borderRadius: 10, boxShadow: '0 1px 4px rgba(0,0,0,0.06)', border: '1px solid #f1f5f9', overflow: 'hidden' }}>
        {/* Tab bar */}
        <div style={{ display: 'flex', borderBottom: '1px solid #f1f5f9', overflowX: 'auto', padding: '0 8px' }}>
          {TABS.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              style={{ padding: '12px 16px', border: 'none', background: 'none', cursor: 'pointer', fontSize: 13, fontWeight: activeTab === tab.id ? 700 : 500, color: activeTab === tab.id ? '#1B2A6B' : '#64748b', borderBottom: `2px solid ${activeTab === tab.id ? '#C9A84C' : 'transparent'}`, marginBottom: -1, whiteSpace: 'nowrap', transition: 'all 0.15s', display: 'flex', alignItems: 'center', gap: 5 }}>
              <span>{tab.icon}</span> {tab.label}
            </button>
          ))}
        </div>

        {/* Tab content */}
        <div style={{ padding: '20px 22px' }}>
          {activeTab === 'overview' && (
            <OverviewTab
              task={task} editForm={editForm} setEditForm={setEditForm}
              editing={editing} setEditing={setEditing} saving={saving}
              handleSave={handleSave} isSupport={isSupport}
              isAdminOrAbove={isAdminOrAbove} isManager={isManager}
              users={users}
              circularDepError={circularDepError}
            />
          )}
          {activeTab === 'updates' && (
            <UpdatesTab task={task} updates={updates} onSubmitted={loadTask} />
          )}
          {activeTab === 'subtasks' && (
            <SubtaskPanel taskId={id} onSubtaskChange={loadTask} />
          )}
          {activeTab === 'timelog' && (
            <TimeLogPanel taskId={id} task={task} />
          )}
          {activeTab === 'files' && (
            <FilesTab
              task={task} isSupport={isSupport} isAdminOrAbove={isAdminOrAbove}
              uploading={uploading} fileInputRef={fileInputRef}
              user={user}
              onUpload={handleUpload} onView={handleView}
              onDownload={handleDownload} onDelete={handleDeleteAttachment}
            />
          )}
          {activeTab === 'activity' && (
            <ActivityTab updates={updates} task={task} />
          )}
        </div>
      </div>
    </div>
  );
}

// ── Overview Tab ─────────────────────────────────────────────────────────────
function OverviewTab({ task, editForm, setEditForm, editing, setEditing, saving, handleSave, isSupport, isAdminOrAbove, isManager, users, circularDepError }) {
  const set = (k, v) => setEditForm(f => ({ ...f, [k]: v }));
  const [tagInput, setTagInput] = useState('');

  const addTag = (e) => {
    if ((e.key === 'Enter' || e.key === ',') && tagInput.trim()) {
      e.preventDefault();
      const tag = tagInput.trim();
      if (!editForm.tags.includes(tag)) set('tags', [...editForm.tags, tag]);
      setTagInput('');
    }
  };

  const canEdit = !isSupport;
  const canAssign = isAdminOrAbove || isManager;

  const inputStyle = { width: '100%', padding: '7px 10px', borderRadius: 7, border: '1.5px solid #e2e8f0', fontSize: 13, color: '#1e293b', outline: 'none', boxSizing: 'border-box' };
  const rowStyle   = { display: 'grid', gridTemplateColumns: '140px 1fr', gap: 8, alignItems: 'start', marginBottom: 14 };
  const labelStyle = { fontSize: 12, fontWeight: 600, color: '#94a3b8', paddingTop: 4 };
  const valueStyle = { fontSize: 13, color: '#1e293b' };

  if (!editing) {
    return (
      <div>
        {canEdit && (
          <div style={{ textAlign: 'right', marginBottom: 16 }}>
            <button onClick={() => setEditing(true)}
              style={{ padding: '7px 18px', borderRadius: 8, border: '1.5px solid #1B2A6B', background: '#fff', color: '#1B2A6B', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>
              ✏️ Edit
            </button>
          </div>
        )}
        <div>
          {[
            ['Title',       task.title],
            ['Description', task.description || '—'],
            ['Status',      task.status],
            ['Priority',    task.priority],
            ['Assigned To', task.assigned_to?.name || '—'],
            ['Due Date',    task.due_date ? new Date(task.due_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'],
            ['Estimated',   task.estimated_hours ? task.estimated_hours + ' hours' : '—'],
            ['Category',    task.category || '—'],
            ['Tags',        task.tags?.length ? task.tags.join(', ') : '—'],
            ['Created By',  task.created_by?.name || '—'],
            ['Created At',  task.createdAt ? new Date(task.createdAt).toLocaleString('en-IN') : '—'],
          ].map(([lbl, val]) => (
            <div key={lbl} style={rowStyle}>
              <div style={labelStyle}>{lbl}</div>
              <div style={valueStyle}>{val}</div>
            </div>
          ))}
          {/* Linked cards */}
          {task.lead_id && (
            <div style={rowStyle}>
              <div style={labelStyle}>Lead</div>
              <Link to={`/leads/${task.lead_id._id || task.lead_id}`} style={{ fontSize: 13, color: '#1B2A6B', textDecoration: 'underline' }}>
                📋 View Lead
              </Link>
            </div>
          )}
          {task.student_id && (
            <div style={rowStyle}>
              <div style={labelStyle}>Student</div>
              <Link to={`/students/${task.student_id._id || task.student_id}`} style={{ fontSize: 13, color: '#1B2A6B', textDecoration: 'underline' }}>
                👤 View Student
              </Link>
            </div>
          )}
          {/* Depends On (read mode) */}
          <div style={rowStyle}>
            <div style={labelStyle}>Depends On</div>
            <div>
              <DependencyPicker
                value={task.depends_on || []}
                onChange={() => {}}
                excludeTaskId={task._id}
                readOnly
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Edit mode
  return (
    <div>
      {/* Title */}
      <div style={{ marginBottom: 14 }}>
        <label style={{ ...labelStyle, display: 'block', marginBottom: 4 }}>Title</label>
        <input style={inputStyle} value={editForm.title} onChange={e => set('title', e.target.value)} />
      </div>
      {/* Description */}
      <div style={{ marginBottom: 14 }}>
        <label style={{ ...labelStyle, display: 'block', marginBottom: 4 }}>Description</label>
        <textarea style={{ ...inputStyle, resize: 'vertical', minHeight: 80 }} value={editForm.description} onChange={e => set('description', e.target.value)} />
      </div>
      {/* Status + Priority */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
        <div>
          <label style={{ ...labelStyle, display: 'block', marginBottom: 4 }}>Status</label>
          <select style={inputStyle} value={editForm.status} onChange={e => set('status', e.target.value)}>
            {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div>
          <label style={{ ...labelStyle, display: 'block', marginBottom: 4 }}>Priority</label>
          <select style={inputStyle} value={editForm.priority} onChange={e => set('priority', e.target.value)}>
            {PRIORITIES.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>
      </div>
      {/* Assigned To */}
      {canAssign && (
        <div style={{ marginBottom: 14 }}>
          <label style={{ ...labelStyle, display: 'block', marginBottom: 4 }}>Assigned To</label>
          <select style={inputStyle} value={editForm.assigned_to} onChange={e => set('assigned_to', e.target.value)}>
            <option value="">— Unassigned —</option>
            {users.map(u => <option key={u._id} value={u._id}>{u.name}</option>)}
          </select>
        </div>
      )}
      {/* Due Date + Estimated Hours */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
        <div>
          <label style={{ ...labelStyle, display: 'block', marginBottom: 4 }}>Due Date</label>
          <input type="date" style={inputStyle} value={editForm.due_date} onChange={e => set('due_date', e.target.value)} />
        </div>
        <div>
          <label style={{ ...labelStyle, display: 'block', marginBottom: 4 }}>Estimated Hours</label>
          <input type="number" min="0" step="0.5" style={inputStyle} value={editForm.estimated_hours} onChange={e => set('estimated_hours', e.target.value)} />
        </div>
      </div>
      {/* Tags */}
      <div style={{ marginBottom: 18 }}>
        <label style={{ ...labelStyle, display: 'block', marginBottom: 4 }}>Tags</label>
        <div style={{ border: '1.5px solid #e2e8f0', borderRadius: 7, padding: '5px 8px', display: 'flex', flexWrap: 'wrap', gap: 5, background: '#fff' }}
          onClick={e => e.currentTarget.querySelector('input')?.focus()}>
          {editForm.tags.map(tag => (
            <span key={tag} style={{ background: '#e0e7ff', color: '#3730a3', borderRadius: 4, padding: '2px 8px', fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}>
              {tag}
              <button type="button" onClick={() => set('tags', editForm.tags.filter(t => t !== tag))}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: '#6366f1', fontSize: 14, lineHeight: 1 }}>×</button>
            </span>
          ))}
          <input style={{ border: 'none', outline: 'none', fontSize: 13, flex: 1, minWidth: 100, padding: '1px 2px' }}
            placeholder="Add tag + Enter" value={tagInput} onChange={e => setTagInput(e.target.value)} onKeyDown={addTag} />
        </div>
      </div>
      {/* Dependencies */}
      <div style={{ marginBottom: 18 }}>
        <label style={{ ...labelStyle, display: 'block', marginBottom: 6 }}>Depends On</label>
        <DependencyPicker
          value={editForm.depends_on || []}
          onChange={deps => set('depends_on', deps)}
          excludeTaskId={task._id}
          circularError={circularDepError}
        />
      </div>

      {/* Buttons */}
      <div style={{ display: 'flex', gap: 10 }}>
        <button onClick={handleSave} disabled={saving}
          style={{ padding: '9px 22px', borderRadius: 8, border: 'none', background: saving ? '#94a3b8' : '#1B2A6B', color: '#fff', fontWeight: 700, fontSize: 13, cursor: saving ? 'not-allowed' : 'pointer' }}>
          {saving ? 'Saving…' : 'Save Changes'}
        </button>
        <button onClick={() => setEditing(false)}
          style={{ padding: '9px 20px', borderRadius: 8, border: '1.5px solid #e2e8f0', background: '#fff', color: '#475569', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
          Cancel
        </button>
      </div>
    </div>
  );
}

// ── Daily Updates Tab ────────────────────────────────────────────────────────
// DailyUpdateForm owns both the form and the past-updates list.
function UpdatesTab({ task, updates, onSubmitted }) {
  return <DailyUpdateForm taskId={task._id} onSubmitted={onSubmitted} />;
}

// ── Files Tab ─────────────────────────────────────────────────────────────────
function FilesTab({ task, isSupport, isAdminOrAbove, uploading, fileInputRef, user, onUpload, onView, onDownload, onDelete }) {
  const attachments = task.attachments || [];

  return (
    <div>
      {/* Upload */}
      {!isSupport && (
        <div style={{ marginBottom: 18 }}>
          <input ref={fileInputRef} type="file" style={{ display: 'none' }}
            accept=".pdf,.docx,.xlsx,.png,.jpg,.jpeg,.txt"
            onChange={onUpload}
          />
          <button onClick={() => fileInputRef.current?.click()} disabled={uploading}
            style={{ padding: '9px 20px', borderRadius: 8, border: '1.5px solid #1B2A6B', background: '#fff', color: '#1B2A6B', fontWeight: 700, fontSize: 13, cursor: uploading ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
            {uploading ? '⏳ Uploading…' : '📎 Upload File'}
          </button>
          <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 5 }}>Supported: PDF, DOCX, XLSX, PNG, JPG, TXT · Max 20 MB</div>
        </div>
      )}

      {/* File list */}
      {attachments.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px 0', color: '#94a3b8' }}>
          <div style={{ fontSize: 36, marginBottom: 10 }}>📎</div>
          <div style={{ fontWeight: 600, fontSize: 13 }}>No attachments yet</div>
          {!isSupport && <div style={{ fontSize: 12, marginTop: 4 }}>Upload a file to get started.</div>}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {attachments.map(att => {
            const canDelete = isAdminOrAbove || String(att.uploaded_by) === String(user?._id) || String(att.uploaded_by?._id) === String(user?._id);
            const canView   = VIEWABLE_TYPES.has(att.file_type);
            return (
              <div key={att._id} style={{ background: '#f8fafc', borderRadius: 8, padding: '10px 14px', border: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 24, flexShrink: 0 }}>{fileIcon(att.file_type)}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{att.original_name}</div>
                  <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>
                    {formatBytes(att.file_size)} · {att.uploaded_by?.name || '—'} · {new Date(att.uploaded_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 5, flexShrink: 0 }}>
                  {canView && (
                    <button onClick={() => onView(att)} title="View"
                      style={{ padding: '5px 10px', borderRadius: 6, border: '1px solid #e2e8f0', background: '#fff', cursor: 'pointer', fontSize: 13 }}>
                      👁 View
                    </button>
                  )}
                  <button onClick={() => onDownload(att)} title="Download"
                    style={{ padding: '5px 10px', borderRadius: 6, border: '1px solid #e2e8f0', background: '#fff', cursor: 'pointer', fontSize: 13 }}>
                    ⬇ Download
                  </button>
                  {canDelete && (
                    <button onClick={() => onDelete(att)} title="Delete"
                      style={{ padding: '5px 10px', borderRadius: 6, border: '1px solid #fee2e2', background: '#fff', cursor: 'pointer', fontSize: 13, color: '#dc2626' }}>
                      🗑
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Activity Log Tab ──────────────────────────────────────────────────────────
function ActivityTab({ updates, task }) {
  const events = [];

  // Creation event
  if (task.createdAt) {
    events.push({ date: new Date(task.createdAt), icon: '🆕', text: `Task created by ${task.created_by?.name || 'unknown'}`, color: '#1B2A6B' });
  }
  // Completion event
  if (task.completed_at) {
    events.push({ date: new Date(task.completed_at), icon: '✅', text: 'Task marked as Done', color: '#16a34a' });
  }
  // Daily updates
  updates.forEach(u => {
    events.push({ date: new Date(u.update_date), icon: '📝', text: `${u.submitted_by?.name || '?'} added update: "${u.status_note?.slice(0, 60)}${u.status_note?.length > 60 ? '…' : ''}"`, color: '#2563eb' });
  });

  events.sort((a, b) => b.date - a.date);

  return (
    <div>
      {events.length === 0 ? (
        <div style={{ color: '#94a3b8', fontSize: 13, padding: '20px 0' }}>No activity yet.</div>
      ) : (
        <div style={{ position: 'relative' }}>
          {/* Timeline line */}
          <div style={{ position: 'absolute', left: 14, top: 0, bottom: 0, width: 2, background: '#f1f5f9' }} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {events.map((ev, i) => (
              <div key={i} style={{ display: 'flex', gap: 12, alignItems: 'flex-start', position: 'relative' }}>
                <div style={{ width: 30, height: 30, borderRadius: '50%', background: '#fff', border: `2px solid ${ev.color}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, flexShrink: 0, zIndex: 1 }}>
                  {ev.icon}
                </div>
                <div style={{ paddingTop: 4 }}>
                  <div style={{ fontSize: 13, color: '#1e293b', fontWeight: 500 }}>{ev.text}</div>
                  <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>
                    {ev.date.toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
